import React from 'react'
import { Row, Col, Skeleton, Form, Select, Input, InputNumber, Switch, Button, Divider, Typography, Space, Popconfirm, message, Checkbox } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import IShelter from '../../../../interfaces/IShelter'
import IEnum from '../../../../interfaces/IEnum'
import { FormComponent, BaseFormComponentProps } from '../../../BaseComponent'
import { SearchRequest, ShelterPaymentConfigSaveRequest } from '../../../../services/api/interfaces/ApiRequest'
import SalesTaxes from './SalesTaxes'
import { normalizeToArray, normalizeToObject } from '../../../../helpers/field-mapping.helper'
import { shelterApiKeySave } from 'services/api'

type ShelterPaymentProps = BaseFormComponentProps & {
  uuid: string,
  data: IShelter,
  error: any,

  paymentModels: IEnum[] | null,
  petsPaginationData: any,

  loading: boolean,
  contentLoading: boolean,
  
  loadPaymentModels: (args: SearchRequest) => void,
  loadPets: (args: SearchRequest) => void,
  loadShelter: (args: SearchRequest) => void,

  dispatchSave: (args: ShelterPaymentConfigSaveRequest) => void
}

export default class ShelterPayment extends FormComponent<ShelterPaymentProps> {

  static defaultProps = {
    loading: false,
    contentLoading: true
  }

  state = {
    loaded: false,
    saving: false,
    loadingShelter: false,
    savePayload: null as ShelterPaymentConfigSaveRequest | null,

    paymentModelKey: '',
    updatePetsPaymentModel: false,


    paymentType: '',
    stripeEnabled: false,
    isACH: undefined,

    apiKey: ''
  }

  formRef: any

  componentDidMount() {
    const { uuid, paymentModels, loadPaymentModels, loadPets } = this.props

    
    if (!paymentModels) {
      loadPaymentModels({
        options: {
          limit:9999
        }
      })
    }

    // Load available pets
    loadPets({
      search: {
        status_key: 'AVAILABLE',
        deleted: false,
        'branch.shelter': {
          has: {
            uuid
          }
        }
      },
      options: {
        limit: 1
      }
    })
    if (!this.props.data) {
      this.setState({
        loadingShelter: true
      })
      this.props.loadShelter({
        search: {
          uuid: this.props.uuid,
          contract_options: {
            with: {
              deleted: false
            }
          },
          organization_question_shelters: {
            with: {
              deleted: false,
              kiosk_reason_id: null,
              question_shelter_species: {
                with: true
              }
            }
          },
          organization_branches: {
            with: true
          }
        },
        options: {
          _force_ids: true
        }
      })
    }
  }

  componentDidUpdate(prevProps: ShelterPaymentProps, prevState) {
    this.validateAccessPermissions()
    this.parseContextErrors()

    let newState = Object.assign({}, this.state)

    // Was loading
    const wasLoading = prevProps.loading && !this.props.loading


    if(this.props.data?.payment_config?.is_ach && this.state.isACH === undefined){
      this.setState({
        isACH: this.props.data?.payment_config?.is_ach
      })
    }


    // Data was loaded already
    if (newState.loaded) {
      // Save has been processed
      if (wasLoading && newState.saving) {
        newState.saving = false
        newState.savePayload = null

        if (this.props.error) {
          message.error(`Failed to save shelter payment config. Reason ${this.props.error}`)
        } else {
          message.success('Shelter payment config saved successfully!')
        }
      }

      // Dispatch save
      if (!this.props.loading && newState.saving && newState.savePayload) {
        this.props.dispatchSave(newState.savePayload)
      }
    }

    // Should load data
    if (!newState.loaded) {
      if (!this.props.loading && !this.props.contentLoading && !this.props.data) {
        this.props.loadShelter({
          search: {
            uuid: this.props.uuid,
            contract_options: {
              with: {
                deleted: false
              }
            },
            organization_question_shelters: {
              with: {
                deleted: false,
                kiosk_reason_id: null,
                question_shelter_species: {
                  with: true
                }
              }
            },
            organization_branches: {
              with: true
            }
          },
          options: {
            _force_ids: true
          }
        })
      }

      // Data loaded
      if (wasLoading || this.props.data) {
        newState.loaded = true

        if (this.props.data) {
          newState.paymentModelKey = this.props.data.payment_model_key
          newState.paymentType = this.props.data.payment_type
          newState.stripeEnabled = this.props.data.stripe_enabled

        }
      }
    }

    if(this.state.loadingShelter){
      if(this.props.data?.payment_type && this.state.paymentType !== this.props.data.payment_type)this.setState({paymentType: this.props.data.payment_type})
      if(this.props.data?.payment_model_key && this.state.paymentModelKey !== this.props.data.payment_model_key)this.setState({paymentModelKey: this.props.data.payment_model_key})
      if(this.props.data?.stripe_enabled && this.state.stripeEnabled !== this.props.data.stripe_enabled)this.setState({stripeEnabled: this.props.data.stripe_enabled})
      this.setState({
        loadingShelter: false
      })
    }
    

    // Update if needed
    if (newState.loaded !== this.state.loaded || newState.saving !== this.state.saving) {
      this.setState(newState)
    }
  }

  handlePaymentModelChange = (paymentModelKey: string) => {
    this.setState({
      paymentModelKey
    })
  }

  handleUpdatePetsChange = (updatePetsPaymentModel: boolean) => {
    this.setState({
      updatePetsPaymentModel
    })
  }

  handlePaymentTypeChange = (paymentType: string) => {
    this.setState({
      paymentType
    })
  }

  handleStripeEnabledChange = (stripeEnabled: boolean) => {
    this.setState({
      stripeEnabled
    })
  }

  handleIsACHChange = (isACH: boolean) => {
    this.setState({
      isACH
    })
  }

  handleCopyToken = () => {
    if (this.state.apiKey) {
      navigator.clipboard.writeText(this.state.apiKey).then(() => {
        message.success('Token copied to clipboard!')
      }).catch(() => {
        message.error('Failed to copy token')
      })
    }
  }

  handleGetApiKey = () => {
    shelterApiKeySave({
      organization_shelter_uuid: this.props.uuid
    }).then((response: any) => {
    console.log(response)
      if (response && response.data?.data && response.data?.data?.key) {
        this.setState({ apiKey: response.data.data.key })
      } else {
        message.error('Failed to get API Key')
      }
    }).catch(() => {
      message.error('Failed to get API Key')
    })
  }

  handleSave = (values: any) => {
    const { uuid } = this.props

    const salesTaxes = normalizeToObject(values.sales_taxes, {
      mapping: {
        mapKey: {
          key: 'type_key'
        }
      }
    }) as any

    if (salesTaxes['ADOPTION_FEE']) {
      salesTaxes['DISCOUNT'] = salesTaxes['ADOPTION_FEE']
      salesTaxes['ADDITION'] = salesTaxes['ADOPTION_FEE']
    }

    let payload: ShelterPaymentConfigSaveRequest = {
      organization_shelter_uuid: uuid,
      payment_config: {
        payment_model_key: this.state.paymentModelKey,
        update_pets_payment_model: values.update_pets_payment_model || false,
        payment_type: values.payment_type,
        sales_taxes: salesTaxes
      }
    }

    if (values.payment_type === 'STRIPE' && values.stripe?.stripe_enabled) {
      // Parse ID to string
      if (values.stripe.stripe_id) {
        values.stripe.stripe_id = values.stripe.stripe_id.toString()
      }

      payload.payment_config['stripe'] = {
        ...values.stripe
      }
    }

    if (values.payment_type === 'CYBERSOURCE' && values.cybersource) {
      payload.payment_config['cybersource'] = {
        ...values.cybersource
      }
    }

    this.setState({
      saving: true,
      savePayload: payload
    })
  }

  render() {
    const { data, petsPaginationData, paymentModels } = this.props
    const { loaded, saving, paymentModelKey, updatePetsPaymentModel, paymentType, stripeEnabled } = this.state
    
    if (!loaded || !data) {
      return <Skeleton />
    }
    const paymentModelsOptions = paymentModels?.map(paymentModel => {
      return {
        title: paymentModel.name,
        value: paymentModel.key
      }
    }) || [] as any

    const paymentConfig = data.payment_config || {}
    const _salesTaxes = { ...(paymentConfig.sales_taxes || {}) }
    if (_salesTaxes['DISCOUNT'] || _salesTaxes['ADDITION']) {
      _salesTaxes['ADOPTION_FEE'] = _salesTaxes['DISCOUNT'] || _salesTaxes['ADDITION']

      delete _salesTaxes['DISCOUNT']
      delete _salesTaxes['ADDITION']
    }

    

    const salesTaxes = normalizeToArray(_salesTaxes, {
      mapping: {
        key: 'type_key'
      }
    })

    const values = {
      ...data,
      sales_taxes: salesTaxes || [],
      cybersource: data.payment_type === 'CYBERSOURCE' ? paymentConfig : null,
      stripe: data.payment_type === 'STRIPE' ? {
        stripe_enabled: data.stripe_enabled,
        stripe_id: data.stripe_id,
        ...paymentConfig
      } : null
    }

    return (
      <Form
        name="payment_config"
        initialValues={values}
        layout="vertical"
        ref={(refs) => this.formRef = refs}
        onFinish={this.handleSave}
      >
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Payment Model"
              name="payment_model_key"
              initialValue={values.payment_model_key}
              rules={[
                {
                  required: true,
                  message: 'Please select a payment model'
                }
              ]}
            >
              <ul>
                <li><b>ADOPTER_PAYS</b>: The adopter always pays for the Adopets Processing Fee</li>
                <li><b>ORGANIZATION_PAYS</b>: The shelter always pays for the Adopets Processing Fee</li>
                <li><b>ADOPTER_CHOOSES</b>: The adopter chooses to pay for the Adopets Processing Fee. If they choose not to pay for it, the shelter will be billed.</li>
                <li><b>OUTSIDE</b>: No Adopets Processing Fee is paid per adoption. This option is for shelters paying annually directly to PetLoyalty.</li>
              </ul>
              <Select
                placeholder="Select a payment model"
                defaultValue={values.payment_model_key}
                allowClear={true}
                options={paymentModelsOptions}
                onChange={this.handlePaymentModelChange}
              />
            </Form.Item>

            {
              paymentModelKey !== data.payment_model_key ? (
                <>
                  <Form.Item
                    label="Update Available Pets"
                    name="update_pets_payment_model"
                    valuePropName="checked"
                    style={{ marginBottom: 5 }}
                  >
                    <Switch onChange={this.handleUpdatePetsChange} />
                  </Form.Item>

                  <Space direction="vertical">
                    {
                      updatePetsPaymentModel ? (
                        <Space direction="vertical">
                          <Typography.Text type="warning">
                            This will update all available pets to the payment model <Typography.Text type="warning" strong>{paymentModelKey}. </Typography.Text>
                            <Typography.Text mark>{petsPaginationData.count} will be updated!</Typography.Text>
                          </Typography.Text>
                        </Space>
                      ) : null
                    }
                  </Space>

                  <Divider />
                </>
              ) : null
            }

            <Form.Item
              label="Payment Platform"
              name="payment_type"
              rules={[
                {
                  required: true,
                  message: 'Please select a payment type'
                }
              ]}
            >
              <Select
                placeholder="Select a payment type"
                options={[
                  {
                    title: 'Stripe',
                    value: 'STRIPE'
                  },
                  {
                    title: 'Cybersource',
                    value: 'CYBERSOURCE'
                  }
                ]}
                allowClear={true}
                onChange={this.handlePaymentTypeChange}
              />
            </Form.Item>

            {
              // Stripe config
              paymentType === 'STRIPE' ? (
                <Col xs={24} sm={12} md={12}>
                  <Row gutter={24}>
                    <Col xs={24} sm={12} md={12}>
                      <Form.Item
                        label="Stripe Enabled"
                        name={['stripe', 'stripe_enabled']}
                        valuePropName="checked"
                      >
                        <Switch onChange={this.handleStripeEnabledChange} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={12}>
                      <Form.Item
                        label="Is ACH enabled?"
                        name={['stripe', 'is_ach']}
                        valuePropName="checked"
                      >
                        <Switch onChange={this.handleIsACHChange} />
                      </Form.Item>
                    </Col>

                    {!this.state.isACH && <Col xs={24} sm={12} md={12}>
                      <Form.Item
                        label="Stripe Account ID"
                        name={['stripe', 'stripe_id']}
                        rules={[
                          {
                            required: stripeEnabled,
                            message: 'Please inform the Stripe ID'
                          }
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>}
                  </Row>
                </Col>
              ) : null
            }

            {
              // Cybersource config
              paymentType === 'CYBERSOURCE' ? (
                <Col xs={24} sm={24} md={24}>
                  <Row gutter={24}>
                    <Col xs={24} sm={8} md={8}>
                      <Form.Item
                        label="API Key ID"
                        name={['cybersource', 'api_key_id']}
                        rules={[
                          {
                            required: true,
                            message: 'Please inform the API Key ID'
                          }
                        ]}
                      >
                        <Input placeholder="Ex: 86f20347-8454-4dcb-aab8-8d3ccf8114af" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={8} md={8}>
                      <Form.Item
                        label="Secret Key"
                        name={['cybersource', 'secret_key']}
                        rules={[
                          {
                            required: true,
                            message: 'Please inform the Secret Key'
                          }
                        ]}
                      >
                        <Input placeholder="Ex: Vs0Ntce2pjNDWIHaPtRZWo2mA4rotLJC8glOg/589Bo=" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={8} md={8}>
                      <Form.Item
                        label="Merchant ID"
                        name={['cybersource', 'merchant_id']}
                        rules={[
                          {
                            required: true,
                            message: 'Please inform the Merchant ID'
                          }
                        ]}
                      >
                        <Input placeholder="Ex: adopets" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              ) : null
            }

            {
              paymentType === 'STRIPE' ? (
                <>
                  <Typography.Title level={4}>Adopets Fee Configuration</Typography.Title>

                  <Row gutter={24}>
                    <Col xs={24} sm={12} md={12}>
                      <Form.Item
                        label="Adopets Fee Percentage"
                        name={['stripe', 'adopets_fee']}
                        rules={[
                          {
                            required: true,
                            message: 'Please inform the Adoptes Fee'
                          }
                        ]}
                      >
                        <InputNumber
                          min={0}
                          addonAfter="%"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                      <Form.Item
                        label="Adopets Fixed Fee"
                        name={['stripe', 'fixed_fee']}
                        rules={[
                          {
                            required: true,
                            message: 'Please inform the Fixed Fee'
                          }
                        ]}
                      >
                        <InputNumber
                          min={0}
                          formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={(value: any) => value.replace(/\$\s?|(,*)/g, '')}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={24}>
                    <Col xs={24} sm={12} md={12}>
                      <Form.Item
                        label="Stripe Percentage Fee"
                        name={['stripe', 'percentage_fee']}
                        rules={[
                          {
                            required: true,
                            message: 'Please inform the Percentage Fee'
                          }
                        ]}
                      >
                        <InputNumber
                          min={0.0}
                          max={1.0}
                          step={0.001}
                          addonAfter="%"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                      <Form.Item
                        label="Stripe Fixed Fee"
                        name={['stripe', 'percentage_fee_diff']}
                        rules={[
                          {
                            required: true,
                            message: 'Please inform the Percentage Fee Diff'
                          }
                        ]}
                      >
                        <InputNumber
                          min={0.0}
                          max={1.0}
                          step={0.1}
                          formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={(value: any) => value.replace(/\$\s?|(,*)/g, '')}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={24}>
                    <Col xs={24} sm={12} md={12}>
                      <Form.Item
                        label="Stripe Credit Card Cover Fee"
                        name={['stripe', 'cover_cc_fee']}
                        valuePropName="checked"
                      >
                        <Checkbox defaultChecked />
                      </Form.Item>
                    </Col>
                  </Row>
                 
                </>
              ) : null
            }

            <Typography.Title level={4}>Payment API Keys</Typography.Title>

            <Space direction="vertical" style={{ width: '100%' }}>
              <Button type="primary" onClick={this.handleGetApiKey} icon={<CopyOutlined />}>Get Payment Api KEY</Button>
              {this.state.apiKey && (
                <>
                  <Input.TextArea
                    value={this.state.apiKey}
                    rows={3}
                    readOnly
                    style={{ fontFamily: 'monospace', marginTop: 8 }}
                  />
                  <Button 
                    type="default" 
                    icon={<CopyOutlined />}
                    onClick={this.handleCopyToken}
                  >
                    Copy Token
                  </Button>
                </>
              )}
            </Space>
          </Col>

          <Col xs={24} md={12}>
            <Typography.Title level={4}>Sales Taxes Rule</Typography.Title>
            <SalesTaxes />
          </Col>
        </Row>

        <Divider />

        <Row>
          <Col span={12} offset={6}>
            <Popconfirm
              placement="topLeft"
              title="Are sure about saving these settings?"
              okButtonProps={{
                htmlType: 'submit',
                loading: saving
              }}
              onConfirm={() => {
                if (this.formRef) {
                  this.formRef.submit()
                }
              }}
            >
              <Button type="primary" loading={saving} size="large" ref={(refs) => this.actionRefs.save = refs} block>
                Save
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Form>
    )
  }
}
